import gql from 'graphql-tag';

/**
 * Volunteer uploads only, because they are the only kind that can name a team.
 *
 * Deliberately a SEPARATE document rather than a `$group_id` added to CREATE_FUZZY_MATCH_REQUEST,
 * which the contact, tag and donor uploads also use. `group_id` is only in the schema once it has
 * been added to Hasura's insert permission column list, and a document naming a field the role
 * cannot see fails validation outright -- so sharing the mutation would take all four upload
 * types down until that permission lands, instead of just this one.
 */
export const CREATE_VOLUNTEER_FUZZY_MATCH_REQUEST = gql`
  mutation InsertVolunteerFuzzyMatchRequest(
    $organization_id: Int
    $user_id: Int
    $file_url: String
    $request_type: String
    $status: String
    $group_id: Int
  ) {
    row: insert_ri_apps_prd_fuzzy_match_requests(
      objects: {
        organization_id: $organization_id
        user_id: $user_id
        file_url: $file_url
        request_type: $request_type
        status: $status
        group_id: $group_id
      }
    ) {
      affected_rows
      returning {
        fuzzy_match_request_id
      }
    }
  }
`;

export const CREATE_FUZZY_MATCH_REQUEST = gql`
  mutation InsertFuzzyMatchRequests(
    $organization_id: Int
    $user_id: Int
    $file_url: String
    $request_type: String
    $status: String
  ) {
    row: insert_ri_apps_prd_fuzzy_match_requests(
      objects: {
        organization_id: $organization_id
        user_id: $user_id
        file_url: $file_url
        request_type: $request_type
        status: $status
      }
    ) {
      affected_rows
      returning {
        fuzzy_match_request_id
      }
    }
  }
`;

export const GET_FUZZY_MATCH_REQUESTS = gql`
  query GetFuzzyMatchRequests($where: ri_apps_prd_fuzzy_match_requests_bool_exp) {
    rows: ri_apps_prd_fuzzy_match_requests(where: $where, order_by: { created_at: desc }) {
      fuzzy_match_request_id
      file_url
      request_type
      status
      created_at
      fuzzy_match_requests_user {
        user_id
        first_name
        last_name
        email
      }
      fuzzy_match_requests_records_aggregate {
        aggregate {
          count
        }
      }
      fuzzy_match_requests_organization {
        organization_id
        name
      }
    }
  }
`;

export const SAVE_FUZZY_MATCH_FIELD_MAPPING = gql`
  mutation SaveFuzzyMatchFieldMapping(
    $objects: [ri_apps_prd_fuzzy_match_field_mapping_insert_input!]!
  ) {
    rows: insert_ri_apps_prd_fuzzy_match_field_mapping(objects: $objects) {
      affected_rows
    }
  }
`;

export const GET_FUZZY_MATCH_RESULTS = gql`
  query GetFuzzyMatchResults(
    $where: ri_apps_prd_fuzzy_match_records_bool_exp
    $limit: Int
    $offset: Int
  ) {
    rows: ri_apps_prd_fuzzy_match_records(
      where: $where
      order_by: { fuzzy_match_record_id: asc }
      limit: $limit
      offset: $offset
    ) {
      fuzzy_match_record_id
      request_id
      first_name
      last_name
      address
      city
      zip
      state
      matching_first_name
      matching_last_name
      matching_address
      matching_city
      matching_zip
      matching_state
      voter_id
      data
      is_approved
      match_confidence
    }
    aggregate: ri_apps_prd_fuzzy_match_records_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_FUZZY_MATCH_RECORDS_COUNT = gql`
  query GetFuzzyMatchRecordsCount($request_id: Int!) {
    exact: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, match_confidence: { _eq: "Exact" } }
    ) {
      aggregate {
        count
      }
    }
    likely: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, match_confidence: { _eq: "Likely" } }
    ) {
      aggregate {
        count
      }
    }
    possible: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, match_confidence: { _eq: "Possible" } }
    ) {
      aggregate {
        count
      }
    }
    possible_family: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, match_confidence: { _eq: "Possible Family" } }
    ) {
      aggregate {
        count
      }
    }
    no_match: ri_apps_prd_fuzzy_match_records_aggregate(
      where: {
        request_id: { _eq: $request_id }
        _or: [{ match_confidence: { _eq: "No match" } }, { match_confidence: { _eq: "" } }]
      }
    ) {
      aggregate {
        count
      }
    }
    approved: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, is_approved: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
    total: ri_apps_prd_fuzzy_match_records_aggregate(where: { request_id: { _eq: $request_id } }) {
      aggregate {
        count
      }
    }
  }
`;

export const APPROVE_FUZZY_MATCH_RECORDS = gql`
  mutation ApproveFuzzyMatchRecords($request_id: Int!, $fuzzy_match_record_ids: [Int!]!) {
    rows: update_ri_apps_prd_fuzzy_match_records(
      where: {
        request_id: { _eq: $request_id }
        fuzzy_match_record_id: { _in: $fuzzy_match_record_ids }
      }
      _set: { is_approved: true }
    ) {
      affected_rows
    }
  }
`;

export const CREATE_FUZZY_MATCH_TAG_MAPPING = gql`
  mutation CreateFuzzyMatchTagMapping(
    $objects: [ri_apps_prd_fuzzy_match_tag_mapping_insert_input!]!
  ) {
    rows: insert_ri_apps_prd_fuzzy_match_tag_mapping(objects: $objects) {
      affected_rows
    }
  }
`;

export const GET_FUZZY_MATCH_TAG_MAPPINGS = gql`
  query GetFuzzyMatchTagMappings($request_id: Int!) {
    rows: ri_apps_prd_fuzzy_match_tag_mapping(where: { request_id: { _eq: $request_id } }) {
      tag_mapping_id
      column_name
      fuzzy_match_tag_mapping_c_tag {
        c_tag_id
        title
      }
      fuzzy_match_tag_mapping_org_tag {
        org_tag_id
        title
      }
      fuzzy_match_tag_mapping_financial_tag {
        tag_id
        title
      }
    }
  }
`;

export const GET_FUZZY_MATCH_FINAL_PROGRESS = gql`
  query GetFuzzyMatchFinalProgress($request_id: Int!) {
    total_approved: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, is_approved: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
    total_all: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id } }
    ) {
      aggregate {
        count
      }
    }
    finalized: ri_apps_prd_fuzzy_match_records_aggregate(
      where: { request_id: { _eq: $request_id }, is_finalized: { _eq: true } }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_FUZZY_MATCH_REQUEST = gql`
  query GetFuzzyMatchRequest($request_id: Int!) {
    rows: ri_apps_prd_fuzzy_match_requests(
      where: { fuzzy_match_request_id: { _eq: $request_id } }
    ) {
      fuzzy_match_request_id
      status
      request_type
      organization_id
      created_at
      file_url
    }
  }
`;

export const GET_FUZZY_MATCH_CONTACT_MAPPINGS = gql`
  query GetFuzzyMatchContactMappings($request_id: Int!) {
    rows: ri_apps_prd_fuzzy_match_field_mapping(
      where: { request_id: { _eq: $request_id }, field_type: { _eq: "contact" } }
    ) {
      source_field
      destination_field
    }
  }
`;

export const GET_FUZZY_MATCH_VOLUNTEER_MAPPINGS = gql`
  query GetFuzzyMatchVolunteerMappings($request_id: Int!) {
    rows: ri_apps_prd_fuzzy_match_field_mapping(
      where: { request_id: { _eq: $request_id }, field_type: { _eq: "volunteer" } }
    ) {
      source_field
      destination_field
    }
  }
`;

export const UPDATE_FUZZY_MATCH_REQUEST = gql`
  mutation UpdateFuzzyMatchRequest($request_id: Int!, $status: String!, $error_message: String) {
    rows: update_ri_apps_prd_fuzzy_match_requests(
      where: { fuzzy_match_request_id: { _eq: $request_id } }
      _set: { status: $status, error_message: $error_message }
    ) {
      affected_rows
    }
  }
`;
